/**
 * API Client Configuration
 * Centralized axios instance with auth interceptor
 */

import axios, { type AxiosInstance, type AxiosError } from 'axios'
import { supabase } from '@/lib/supabase'

// API URLs
export const SUPABASE_URL = process.env.VUE_APP_SUPABASE_URL || 'https://sinechart.de:8000'
export const BACKEND_URL = process.env.VUE_APP_API_URL || 'https://sinechart.de:5991/api'

/**
 * Supabase client for direct database access
 * Use for simple CRUD operations
 */
export { supabase }

/**
 * Backend API client for complex operations
 * Use for filtering, aggregations, and business logic
 */
export const backendClient: AxiosInstance = axios.create({
  baseURL: BACKEND_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to all backend requests
backendClient.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession()

  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`
  }

  return config
})

// Handle errors globally
backendClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Log error for debugging
    console.error('API Error:', error.response?.status, error.response?.data)

    // Handle specific error codes
    if (error.response?.status === 401) {
      // Token expired or invalid
      console.warn('Unauthorized - redirecting to login')
      // Could trigger logout or redirect here
    }

    return Promise.reject(error)
  }
)

/**
 * Helper to handle Supabase errors
 */
export function handleSupabaseError(error: any): never {
  console.error('Supabase Error:', error)
  throw new Error(error.message || 'Database operation failed')
}
